// Shared UI pieces. Small, unclever, and all in one file so the whole visual
// vocabulary of the app can be read in one sitting.

import { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft } from 'lucide-react';
import { useToast } from '../lib/store.js';

export function Card({ children, className = '', as: Tag = 'div', ...rest }) {
  return (
    <Tag className={`card ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <div className="flex items-baseline justify-between mb-2">
      <h2 className="section-title mb-0">{children}</h2>
      {action}
    </div>
  );
}

export function Stat({ label, value, sub, tone = 'default' }) {
  const toneClass = { default: 'text-text', good: 'text-good', warn: 'text-warn', crit: 'text-crit', accent: 'text-accent' }[tone];
  return (
    <div>
      <div className="text-label uppercase text-textMute mb-1">{label}</div>
      <div className={`text-xl font-semibold ${toneClass}`}>{value}</div>
      {sub && <div className="text-meta text-textMute mt-0.5">{sub}</div>}
    </div>
  );
}

export function Pill({ children, tone = 'mute' }) {
  return <span className={`pill-${tone}`}>{children}</span>;
}

export function Button({ children, variant = 'primary', size, className = '', ...rest }) {
  return (
    <button className={`btn-${variant} ${size === 'sm' ? 'btn-sm' : ''} ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function Field({ label, hint, children, id }) {
  return (
    <div className="mb-4">
      {label && (
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
      )}
      {children}
      {hint && <p className="text-meta text-textMute mt-1.5">{hint}</p>}
    </div>
  );
}

export function TextInput({ id, ...rest }) {
  return <input id={id} className="field" {...rest} />;
}

export function NumberInput({ id, suffix, ...rest }) {
  return (
    <div className="relative">
      <input id={id} type="number" inputMode="decimal" className="field pr-12" {...rest} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-body-sm text-textMute">{suffix}</span>}
    </div>
  );
}

export function TextArea({ id, rows = 3, ...rest }) {
  return <textarea id={id} rows={rows} className="field resize-y" {...rest} />;
}

export function Select({ id, children, ...rest }) {
  return (
    <select id={id} className="field" {...rest}>
      {children}
    </select>
  );
}

// A choice rendered as buttons rather than a dropdown — one tap, and the
// options stay visible, which matters on a phone in a garden gym.
export function Choice({ options, value, onChange, columns = 0 }) {
  // Inline grid-template-columns rather than a `grid-cols-${n}` class, which
  // Tailwind cannot see at build time and would silently drop.
  return (
    <div
      className={columns ? 'grid gap-2' : 'flex flex-wrap gap-2'}
      style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
    >
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        const active = value === val;
        return (
          <button
            key={String(val)}
            type="button"
            onClick={() => onChange(val)}
            aria-pressed={active}
            className={`px-3 py-2.5 rounded-control border text-body-sm font-medium transition-colors ${
              active ? 'bg-accent text-onAccent border-accent' : 'bg-panel text-textSoft border-edge hover:border-edgeHi'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div className="text-center py-10 px-6">
      {Icon && <Icon className="mx-auto mb-3 text-textMute" size={28} strokeWidth={1.5} />}
      <p className="font-display text-card-title mb-1">{title}</p>
      {body && <p className="text-body-sm text-textSoft max-w-xs mx-auto">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = 'h-24' }) {
  return <div className={`skeleton ${className}`} />;
}

export function Spinner({ label = 'Loading' }) {
  return (
    <div className="py-12 text-center text-body-sm text-textMute" role="status">
      {label}…
    </div>
  );
}

export function ErrorNote({ error, onRetry }) {
  if (!error) return null;
  return (
    <Card className="border-crit/40">
      <p className="text-body-sm text-crit mb-3">{typeof error === 'string' ? error : error.message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </Card>
  );
}

// Bottom sheet. Used for every form in the app — it keeps the page underneath
// visible, which makes logging feel like an aside rather than a detour.
export function Sheet({ open, onClose, title, children, footer }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full sm:max-w-lg bg-panel border-t sm:border border-edge sm:rounded-card rounded-t-card
                   max-h-[92vh] overflow-y-auto animate-fade-up shadow-lift outline-none"
      >
        <div className="sticky top-0 bg-panel border-b border-edge px-card py-3 flex items-center justify-between">
          <h2 className="text-card-title font-display">{title}</h2>
          <button onClick={onClose} className="p-2 -mr-2 text-textMute hover:text-text" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="px-card py-card">{children}</div>
        {footer && <div className="sticky bottom-0 bg-panel border-t border-edge px-card py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, back, right }) {
  return (
    <header className="pt-6 pb-4 px-gutter flex items-start gap-3">
      {back && (
        <button onClick={back} className="p-2 -ml-2 mt-0.5 text-textSoft hover:text-text" aria-label="Back">
          <ChevronLeft size={22} />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <h1 className="text-page-title">{title}</h1>
        {subtitle && <p className="text-body-sm text-textSoft mt-1">{subtitle}</p>}
      </div>
      {right}
    </header>
  );
}

export function Toast() {
  const { toast, clear } = useToast();
  if (!toast) return null;
  const tone = { good: 'bg-good text-white', warn: 'bg-warn text-white', crit: 'bg-crit text-white' }[toast.tone] || 'bg-text text-bg';
  return (
    <div
      role="status"
      aria-live="polite"
      onClick={clear}
      className={`fixed left-1/2 -translate-x-1/2 bottom-24 z-[60] px-4 py-3 rounded-control shadow-lift text-body-sm font-medium animate-fade-up ${tone}`}
    >
      {toast.message}
    </div>
  );
}

// A tiny inline line chart. No chart library: one series, one path, and a
// shaded area — which is all any screen here needs.
export function Sparkline({ points, height = 60, tone = 'accent', showDots = false, label }) {
  const values = (points || []).filter((p) => p.value != null);
  if (values.length < 2) {
    return <div className="h-[60px] flex items-center text-meta text-textMute">Not enough readings yet.</div>;
  }

  const width = 300;
  const min = Math.min(...values.map((p) => p.value));
  const max = Math.max(...values.map((p) => p.value));
  const span = max - min || 1;
  const pad = 6;

  const xy = values.map((p, i) => [
    (i / (values.length - 1)) * (width - pad * 2) + pad,
    height - pad - ((p.value - min) / span) * (height - pad * 2),
  ]);

  const line = xy.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${xy[xy.length - 1][0].toFixed(1)},${height} L${xy[0][0].toFixed(1)},${height} Z`;
  const stroke = `var(--c-${tone})`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} role="img" aria-label={label || 'Trend'}>
      <path d={area} fill={stroke} opacity="0.1" />
      <path d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {showDots && xy.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2" fill={stroke} />)}
      <circle cx={xy[xy.length - 1][0]} cy={xy[xy.length - 1][1]} r="3.5" fill={stroke} />
    </svg>
  );
}

// Horizontal progress, used for steps and protein against target.
export function Meter({ value, target, tone = 'accent', label, valueLabel }) {
  const pct = target ? Math.min(100, Math.round(((value || 0) / target) * 100)) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-body-sm text-textSoft">{label}</span>
        <span className="text-body-sm font-semibold">{valueLabel}</span>
      </div>
      <div
        className="h-2 rounded-full bg-panelHi overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `var(--c-${tone})` }} />
      </div>
    </div>
  );
}

// Used on the session screen: tap to tick a set off.
export function useLocalState(initial) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  return [value, setValue];
}
